import React, { useCallback, useMemo, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useStaffCan, useSelector } from '~Hooks';
import { useHistory } from 'react-router';
import * as actions from '../../actions';
import TallView from './Components/Tall';
import { useStorage } from '~Components/Storage';

import Responsive from './Components/Responsive';
import useFilterOrders from '~Scenes/EventedKitchenDisplay/Hooks/useFilterOrders';

const isCooking = (orderStatus: string) =>
  orderStatus === 'ACCEPTED' || orderStatus === 'FINALIZING';

const isReady = (orderStatus: string) => orderStatus === 'READY';

interface MappedOrder {
  id: string;
  status: string;
  easyId: string;
}

/** Returns the same array reference across renders as long as each item's`id`/`easyId` matches by position. */
const useStableOrders = (orders: MappedOrder[]): MappedOrder[] => {
  const ref = useRef(orders);
  const same =
    ref.current.length === orders.length &&
    ref.current.every((p, i) => p.id === orders[i].id && p.easyId === orders[i].easyId);
  if (!same) {
    ref.current = orders;
  }
  return ref.current;
};

interface Props {
  useResponsiveDisplay?: boolean;
}

const CustomerDisplay: React.FunctionComponent<Props> = ({ useResponsiveDisplay }: Props) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const staffCan = useStaffCan();
  const mainMenu = useSelector((state) => state.mainMenu);
  const [storage] = useStorage();

  /**
   * Get current station
   */
  const station = useSelector((state) => {
    if (!state.stations || !storage.selectedKDSStation) {
      return undefined;
    }

    return state.stations.find((s) => s.id === storage.selectedKDSStation);
  });

  /**
   * Stable across order changes so the memoized Responsive child doesn't
   * re-render every time orders update. `staffCan.openMainMenu` and
   * `mainMenu` are listed as primitive deps; `dispatch` and `history` are
   * already stable from their respective providers.
   */
  const toggleMenu = useCallback(() => {
    if (staffCan.openMainMenu) {
      dispatch(actions.toggleMainMenu(!mainMenu));
    } else {
      // If staff does not have permission to open menu just send them back to venue menu
      history.push('gateway');
    }
  }, [staffCan.openMainMenu, mainMenu, dispatch, history]);

  const filteredOrders = useFilterOrders({ station });

  const orders = useMemo(
    () =>
      filteredOrders.map((order) => ({
        id: order.id,
        status: order.status,
        easyId: order.tableName || order.realId!,
      })),
    [filteredOrders],
  );

  /**
   * Filter runs each render but the result ref is stabilized by
   * `useStableOrders`, so Responsive only re-renders when the visible set of
   * cooking/ready `id`/`easyId` actually shifts.
   */
  const cooking = useStableOrders(
    orders.filter((order) =>
      orders.filter((o) => o.easyId === order.easyId).find((match) => isCooking(match.status)),
    ),
  );

  const ready = useStableOrders(
    orders.filter((order) =>
      orders.filter((o) => o.easyId === order.easyId).find((match) => isReady(match.status)),
    ),
  );

  if (useResponsiveDisplay) {
    return <Responsive cooking={cooking} ready={ready} toggleMenu={toggleMenu} />;
  }

  return <TallView loading={false} cooking={cooking} ready={ready} toggleMenu={toggleMenu} />;
};

export default CustomerDisplay;
